import { BreakpointObserver } from "@angular/cdk/layout";
import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MatDrawerMode } from "@angular/material/sidenav";
import { NavigationEnd, Router } from "@angular/router";

import { distinctUntilChanged, filter, map, pairwise, skip } from "rxjs/operators";

import { SLUGS } from "#shared/slugs";

@Injectable({ providedIn: "root" })
export class SidenavStateService {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private readonly modeToggleWidth = 600;
  private readonly openToggleWidth = 900;

  private readonly modeBreakPoint = `(min-width: ${this.modeToggleWidth}px)`;
  private readonly openBreakPoint = `(min-width: ${this.openToggleWidth}px)`;

  // Explicit user override; null means "follow the automatic state".
  private readonly userState = signal<boolean | null>(null);
  // Closed on load and auto-close on home page; opened on the first subpage
  // navigation (see constructor) or explicitly by the user.
  private readonly autoState = signal(false);

  readonly opened = computed(() => this.userState() ?? this.autoState());

  readonly mode = toSignal(
    this.breakpointObserver.observe(this.modeBreakPoint).pipe(map((result): MatDrawerMode => (result.matches ? "side" : "over"))),
    { initialValue: (this.breakpointObserver.isMatched(this.modeBreakPoint) ? "side" : "over") as MatDrawerMode }
  );

  constructor() {
    // skip(1) drops the immediate emission for the current viewport so the nav
    // stays closed on load; crossing the breakpoint later still toggles it.
    this.breakpointObserver
      .observe(this.openBreakPoint)
      .pipe(
        map((result) => result.matches),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((matches) => this.autoState.set(matches));

    // Open the nav the first time the user navigates to a subpage (anything
    // below the home overview). Skip the initial navigation so a page load —
    // including deep links — always starts with the nav closed, and skip small
    // viewports where the nav would cover the content as an overlay.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        pairwise(),
        filter(([prev, curr]) => {
          // Parse URLs into Angular UrlTree objects
          const prevTree = this.router.parseUrl(prev.urlAfterRedirects);
          const currTree = this.router.parseUrl(curr.urlAfterRedirects);

          // Strip out query params and fragments
          prevTree.queryParams = {};
          prevTree.fragment = null;

          currTree.queryParams = {};
          currTree.fragment = null;

          // Compare the serialized base paths
          return prevTree.toString() !== currTree.toString();
        }),
        map(([prev, curr]) => curr),
        filter((e) => !this.isHomeUrl(e.urlAfterRedirects)),
        filter(() => this.breakpointObserver.isMatched(this.openBreakPoint)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.autoState.set(true);
      });
  }

  private isHomeUrl(url: string): boolean {
    const path = url.split("?")[0].replace(/^\/+|\/+$/g, "");
    return path === "" || path === SLUGS.home;
  }

  open() {
    /* if was auto-closed then this is an explicit close */
    if (!this.autoState()) {
      this.userState.set(true);
    } else {
      this.userState.set(null);
    }
  }

  close() {
    /* if was auto-opened then this is an explicit close */
    if (this.autoState()) {
      this.userState.set(false);
    } else {
      this.userState.set(null);
    }
  }

  autoClose() {
    this.autoState.set(false);
  }

  toggle() {
    if (this.opened()) {
      this.close();
    } else {
      this.open();
    }
  }
}
